/**
 * Chrome for the full-screen book reader (/book/:owner/:repo).
 *
 * It puts the one canonical SystemDoxHeader on top — the same bar as every
 * other surface (brand → launch home, surface switcher, global search, scope
 * selector, notifications, account) — then renders the reader body beneath it.
 *
 * Like TenantShell it renders the BottomNav so navigation is reachable from the
 * reader. It also mounts the same light, always-on
 * providers the header needs (Tenant + Project, both fail-soft, plus the
 * notification providers so the bell works here too), which the reader route
 * would otherwise lack outside TenantShell.
 */

import { useNavigate } from 'react-router-dom';
import { type ReactNode } from 'react';
import {
  NotificationProvider,
  NotificationOverlayProvider,
  NotificationPanel,
  useNotificationOverlay,
} from '@puglieseweb/shared-react-notifications';
import { useAuthContext } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { ProjectProvider } from '@/contexts/ProjectContext';
import { BookmarksProvider } from '@/contexts/BookmarksContext';
import { SystemDoxHeader } from '@/components/layout/SystemDoxHeader';
import { BottomNav } from '@/components/layout/BottomNav';
import {
  systemdoxNotificationAdapter,
  notificationActionUrl,
} from '@/services/notificationAdapter';

function ReaderShellInner({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { isOpen, toggle, close } = useNotificationOverlay();

  return (
    // text-foreground pairs with bg-background — see TenantShell.
    <div className="h-dvh flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 flex shrink-0 items-center border-b border-border bg-card px-4 py-3">
        <SystemDoxHeader
          showSearch
          showNotifications
          onNotificationsClick={toggle}
          settingsTo="/settings"
          notificationsTo="/notifications"
        />
      </header>

      {/* Reader body — bounded and non-scrolling; the reader owns its scroll. */}
      <div className="flex flex-1 min-h-0 flex-col">{children}</div>

      <BottomNav />

      <NotificationPanel
        isOpen={isOpen}
        onClose={close}
        onNotificationClick={(notification) => {
          // Durable notifications carry their link in job.meta, not actionUrl.
          const url = notificationActionUrl(notification);
          if (url) navigate(url);
        }}
      />
    </div>
  );
}

export function ReaderShell({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();

  return (
    <NotificationProvider
      config={{
        adapter: systemdoxNotificationAdapter,
        isAuthenticated: !!user,
      }}
    >
      <NotificationOverlayProvider>
        <TenantProvider>
          <ProjectProvider>
            <BookmarksProvider>
              <ReaderShellInner>{children}</ReaderShellInner>
            </BookmarksProvider>
          </ProjectProvider>
        </TenantProvider>
      </NotificationOverlayProvider>
    </NotificationProvider>
  );
}
